import { Component, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormGroup, UntypedFormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import { Client } from 'src/providers/client/client';
import { Loading } from 'src/shared/components/loading/loading';
import { Genome } from 'src/providers/genome/genome';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { PhotoNotes } from 'src/providers/photo-notes/photo-notes';
import { PhotoCaptionService } from 'src/shared/components/photo-caption/photo-caption.service';
import { Util } from 'src/shared/util/util';
import { AssetsQueriesService } from 'src/shared/services/assets-queries.service';
import { v4 as uuidv4 } from 'uuid';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';

import { validateGreaterThanOrEqual } from 'src/shared/validator/validateGreaterThanOrEqual';

import { floorSelect } from 'src/shared/constants/floorSelect';
import { Capacitor } from '@capacitor/core';
import { DateTime as LuxonDateTime } from 'luxon';
import { ItemDetailsPage } from '../room-item-details/room-item-details';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

import { PreferredBrand, isPreferredBrandBlank, isPreferredBrandIncomplete } from 'src/models/restocking-brand.model';

@Component({
  templateUrl: 'add-item-form.html',
  standalone: false
})

export class AddItemFormPage implements OnInit {
  private readonly assetsQueries = inject(AssetsQueriesService);

  addressItems: any;
  category: any;
  dialogParams: any;
  installationDateItems: any;
  form: UntypedFormGroup;
  floorSelectItems = floorSelect;
  submitted: boolean;
  categories: any;
  categoryChosen: boolean;
  categoriesResponse: any;
  characteristics: { name: String, formData: { value: number, viewValue: String } }[];
  characteristicsChosen: { [id: string]: number } = {};
  hasNoItemsOrMap: boolean;
  loaded: boolean;
  models: any = [];
  isRightSideContent: boolean;
  getInventoryAlerts: boolean;
  createNewModel: boolean;
  characteristicsArray: any = [];
  salesCards: any;
  selectedModelID: number;
  showAddressSelect: boolean;
  showConsumables: boolean;
  selectedWhereToOrderName: string;
  deliveryTimingPreferenceItems = [
    { value: 'Between guest stays', viewValue: new TranslationPipe().transform('Between guest stays') },
    { value: 'As soon as possible', viewValue: new TranslationPipe().transform('As soon as possible') }
  ];
  photoURL: string;
  inventoryUpdateTypeItems = [
    { value: 'manual', viewValue: new TranslationPipe().transform('Manually') },
    { value: 'task', viewValue: new TranslationPipe().transform('Using checklists') },
    { value: 'auto', viewValue: new TranslationPipe().transform('Automatically after each reservation') }
  ];
  kindOfInventoryUpdateItems = [
    { value: 'count_remaining', viewValue: new TranslationPipe().transform('Count remaining') },
    { value: 'count_used', viewValue: new TranslationPipe().transform('Count used') }
  ];
  categoryItems: any;
  toDoListsItems: any = {};
  isUpdatingToDoListsForms = false;
  selectedToDoLists: any = [];
  item: any;
  isAddingNewModel = false;
  didNotSelectRoomError = false;
  boundAddItem = this.addItem.bind(this);
  strategies = [];
  warehousePropertyItems: any[] = [];
  allConsumableRoomObjects: any[] = [];
  whereToOrderItems: any[] = [];
  preferredBrands: PreferredBrand[] = [];
  preferredBrandsAreDefault = false;
  // Drives the "there are errors above" summary under the submit button, so a
  // failed save isn't silent when the offending field has scrolled out of view.
  hasFormErrors = false;
  // The warning level is below the min quantity — a rule RoomObject enforces
  // server-side, mirrored here so it shows inline instead of as a 422.
  warningBelowMinError = false;
  // The target is below the min quantity.
  targetBelowMinError = false;
  // Server-side failure from the last save attempt, kept on screen under the
  // button after the toast auto-dismisses.
  errorMessage: string;

  constructor(
    private client: Client,
    private fb: UntypedFormBuilder,
    private genome: Genome,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private photoNotes: PhotoNotes,
    private photoCaptionService: PhotoCaptionService,
    private util: Util,
    private toDos: ToDos,
    private guestReservations: GuestReservations
  ) {
    // Seed the layout synchronously from the panel's open state. Otherwise the
    // template renders the mobile layout (`@if (!isRightSideContent)`) for one
    // frame until the async storage read in ngOnInit resolves, which flashes the
    // wrong layout when re-entering this screen from the Create Order panel.
    this.isRightSideContent = this.rightSidePanelService.isOpen$.getValue();
    this.form = this.fb.group({
      search: [''],
      characteristic: [''],
      model: [''],
      installation_date: [''],
      minQuantity: [''],
      maxQuantity: [''],
      currentQuantity: [''],
      opportunisticOrderLevel: [''],
      minRestockingPrice: [''],
      maxRestockingPrice: [''],
      whereToOrder: [''],
      deliveryTimingPreferences: [''],
      restockingNotes: [''],
      address: [''],
      inventoryUpdateType: [''],
      kindOfInventoryUpdate: [''],
      category: [''],
      toDoLists: [''],
      itemsPerReservation: [''],
      conciergeLogin: [''],
      conciergePassword: [''],
      conciergeNotes: [''],
      whereToOrderOther: [''],
      warehouseProperty: [''],
    });
    this.form.get('model').valueChanges.pipe(
      debounceTime(100),
      takeUntilDestroyed()
    ).subscribe(val => {
      if (val === '' || val === null) {
        this.selectedModelID = null;
        this.isAddingNewModel = true;
      } else if (val && typeof val === 'string') {
        const matchingOption = this.models.find(option => option.display === val);
        if (matchingOption) {
          this.selectedModelID = matchingOption.value;
          this.isAddingNewModel = false;
        } else {
          this.selectedModelID = null;
          this.isAddingNewModel = true;
        }
      }
    });
    // The warning level and the target are compared against the min, so editing
    // the min has to re-run their validators — Angular only re-runs a validator
    // when its own control changes.
    this.form.get('minQuantity').valueChanges.pipe(
      takeUntilDestroyed()
    ).subscribe(() => {
      this.form.controls.opportunisticOrderLevel.updateValueAndValidity();
      this.form.controls.maxQuantity.updateValueAndValidity();
    });
    this.form.statusChanges.pipe(
      takeUntilDestroyed()
    ).subscribe(() => this.refreshFormErrorState());
  }

  // Keeps the inline messages and the summary under the submit button in sync as
  // the user fixes fields. Only meaningful once they've tried to save — before
  // that nothing is flagged.
  refreshFormErrorState() {
    this.warningBelowMinError = this.form.controls.opportunisticOrderLevel.hasError('lessThanSibling');
    this.targetBelowMinError = this.form.controls.maxQuantity.hasError('lessThanSibling');
    this.hasFormErrors = this.submitted &&
      (!this.form.valid || this.didNotSelectRoomError || this.hasIncompletePreferredBrand());
  }

  @Loading('', true)
  async ngOnInit() {
    try {
      this.loaded = false;
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      await this.buildWhereToOrderItems();
      if (this.isRightSideContent) {
        this.dialogParams = await this.storage.retrieve('dialog-params');
        this.showConsumables = this.dialogParams?.showConsumables;
        this.category = this.dialogParams?.category;
        this.showAddressSelect = this.dialogParams?.showAddressSelect;
      } else {
        this.showConsumables = this.navCtrl.getParam('showConsumables');
        this.category = this.navCtrl.getParam('category');
        this.showAddressSelect = this.navCtrl.getParam('showAddressSelect');
      }
      this.item = this.dialogParams?.item || this.navCtrl.getParam('item');
      this.setValidators();
      if (!this.item) {
        this.buildCategoryItems();
        this.form.patchValue({
          category: this.category.id,
        });
        this.createAddressForm();
        if (this.showConsumables) {
          this.patchFormWithCategoryDefaults();
        } else {
          const defaultYear = Genome.getDefaultInstallationYear(this.category.name);
          this.form.patchValue({
            installation_date: defaultYear
          });
        }
      }
      this.installationDateItems = this.buildInstallationDateItems();
      this.salesCards = this.getSalesCards();
      this.form.patchValue({
        kindOfInventoryUpdate: 'count_remaining',
      })
      if (this.item) {
        this.form.patchValue({
          address: [this.item.address_id]
        });
        const addresses = await this.client.getMoreDetailAddresses(false);
        this.addressItems = this.client.parseAddressList(addresses, false);
        await this.patchItemForm();
      }
      if (this.showConsumables) {
        await this.applyDefaultPreferredBrands();
      }
      this.loaded = true;
    } catch (err) {
      this.util.showError(this.getErrorMessage(err));
    }
  }

  async buildWhereToOrderItems() {
    try {
      this.whereToOrderItems = [];
      const [allSources, addedSources] = await Promise.all([
        this.guestReservations.getSourceTypes(),
        this.guestReservations.getReservationSource()
      ]);
      const allRestockingSources = allSources.filter(type => {
        const typeWithDetails = type as any;
        return typeWithDetails.active_details?.some(
          (detail: any) => detail.key === 'category' && detail.value === 'Restocking'
        );
      });
      const addedRestockingSources = addedSources.filter(source => {
        const sourceTypeId = source.guest_reservation_source_type?.id;
        const isRestockingType = allRestockingSources.some(
          type => type.id === sourceTypeId
        );
        return isRestockingType && source.state !== 'paused';
      });
      const availableRestockingSources = allRestockingSources.filter(type => {
        const alreadyActive = addedSources.some(
          source => source.guest_reservation_source_type?.id === type.id && source.state !== 'paused'
        );
        return !alreadyActive;
      });
      if (addedRestockingSources.length > 0) {
        addedRestockingSources.forEach(source => {
          this.whereToOrderItems.push({
            viewValue: source.guest_reservation_source_type?.name,
            value: `integration:${source.id}`,
            icon: source.guest_reservation_source_type?.logo_url
          });
        });
      }
      if (availableRestockingSources.length > 0) {
        availableRestockingSources.forEach(type => {
          this.whereToOrderItems.push({
            viewValue: `${type.name}`,
            value: `add:${type.id}`,
            icon: type.logo_url
          });
        });
      }
    } catch (_err) {
      // Silently handle error
    }
    this.whereToOrderItems.push({
      viewValue: new TranslationPipe().transform('Other'),
      value: 'other'
    });
  }

  selectWhereToOrder(value) {
    this.selectedWhereToOrderName = this.whereToOrderItems.find(item => item.value === value)?.viewValue;
    if (value === 'other') {
      this.form.controls.conciergeLogin.clearValidators();
      this.form.controls.conciergePassword.clearValidators();
      this.form.controls.conciergeNotes.clearValidators();
      this.form.controls.whereToOrderOther.setValidators([Validators.required]);
    } else if (value.startsWith('add:')) {
      this.form.controls.conciergeLogin.setValidators([Validators.required]);
      this.form.controls.conciergePassword.setValidators([Validators.required]);
      this.form.controls.conciergeNotes.setValidators([Validators.required]);
      this.form.controls.whereToOrderOther.clearValidators();
    }
  }

  async patchItemForm() {
    this.selectedModelID = this.item.model.id;
    this.form.patchValue({
      address: [this.item.address_id],
      model: this.item?.name || '',
      minQuantity: this.item.min_quantity?.toString(),
      maxQuantity: this.item.max_quantity?.toString(),
      currentQuantity: this.item.current_quantity?.toString(),
      inventoryUpdateType: this.item.inventory_settings?.strategy || 'manual',
      kindOfInventoryUpdate: this.item.inventory_settings?.inventory_type,
      itemsPerReservation: this.item.inventory_settings?.strategies?.find(s => s.type === 'auto')?.amount_per_reservation?.toString(),
      restockingNotes: this.item.restocking_notes,
      deliveryTimingPreferences: this.item.delivery_timing_preference,
      whereToOrder: this.item.where_to_order,
      minRestockingPrice: this.item.min_restocking_price?.toString(),
      maxRestockingPrice: this.item.max_restocking_price?.toString(),
      opportunisticOrderLevel: this.item.opportunistic_quantity?.toString(),
    })
    const taskStrategy = this.item.inventory_settings?.strategies?.find(s => s.type === 'task');
    if (taskStrategy) {
      this.buildSelectedListsAndRooms(taskStrategy);
    }
    this.preferredBrands = (this.item?.preferred_brands || []).map((brand) => ({ ...brand }));
    this.photoURL = this.item.photo_url;
    await this.createToDoListForm()
    this.strategies = this.item.inventory_settings?.strategies?.map((strategy) => strategy.type) || [];
    await this.buildWarehousePropertyOptions();
  }

  // Seed the item with default brands for its category (curated on the frontend,
  // keyed by category name). The user can edit them. When not forced, only fills
  // while the tiers are empty or still the untouched defaults, so it never
  // overwrites edits. `force` is used on category change, where the brands should
  // always reset to the new category's defaults.
  async applyDefaultPreferredBrands(force = false) {
    if (!force && this.preferredBrands.length && !this.preferredBrandsAreDefault) {
      return;
    }
    const categoryId = this.item?.model?.category?.id || this.category?.id;
    const defaults = await this.resolveDefaultPreferredBrands(categoryId);
    this.preferredBrands = defaults.map((brand, index) => ({
      brand: brand.brand,
      product_name: brand.product_name,
      link: brand.link,
      notes: brand.notes,
      position: index + 1
    }));
    this.preferredBrandsAreDefault = this.preferredBrands.length > 0;
  }

  // Defaults come from the gotidy customer endpoint (source of truth). No local
  // fallback — a category with no configured defaults starts with an empty
  // waterfall rather than brands the customer never chose.
  async resolveDefaultPreferredBrands(categoryId): Promise<PreferredBrand[]> {
    if (!categoryId) {
      return [];
    }
    try {
      const apiDefaults = await this.genome.getPreferredBrandDefaults(categoryId);
      return (apiDefaults || []).map((brand) => ({ ...brand }));
    } catch (_err) {
      return [];
    }
  }

  onPreferredBrandsChange(brands: PreferredBrand[]) {
    this.preferredBrands = brands;
    this.preferredBrandsAreDefault = false;
    this.refreshFormErrorState();
  }

  // Drop tiers with no brand entered and renumber the waterfall before saving.
  normalizePreferredBrands(): PreferredBrand[] {
    return this.preferredBrands
      .filter((brand) => !!brand.brand?.trim())
      .map((brand, index) => ({ ...brand, position: index + 1 }));
  }

  hasIncompletePreferredBrand(): boolean {
    return this.preferredBrands.some(
      (brand) => !isPreferredBrandBlank(brand) && isPreferredBrandIncomplete(brand)
    );
  }

  async buildWarehousePropertyOptions() {
    try {
      this.allConsumableRoomObjects = await this.genome.getAllConsumableRoomObjects();
      const matchingItems = this.allConsumableRoomObjects.filter(
        (obj) => obj.name === this.item.name && obj.id !== this.item.id && obj.current_quantity !== null
      );
      this.warehousePropertyItems = matchingItems.map((obj) => ({
        value: obj.address_id,
        viewValue: obj.address?.address_name || obj.address?.address1 || `Property ${obj.address_id}`,
        roomObjectId: obj.id
      }));
      if (this.item.warehouse_room_object_id) {
        const warehouseItem = this.allConsumableRoomObjects.find(
          (obj) => obj.id === this.item.warehouse_room_object_id
        );
        if (warehouseItem) {
          this.form.patchValue({
            warehouseProperty: warehouseItem.address_id
          });
        }
      }
    } catch (err) {
      // Silently handle error - warehouse selection is optional
    }
  }

  buildSelectedListsAndRooms(taskStrategy) {
    taskStrategy.address_task_lists.map((list) => {
      this.selectedToDoLists.push({addressId: this.item.address_id, listId: list.address_task_list_id, rooms: list.address_task_group_ids});
    });
  }

  buildCategoryItems() {
    const categories = this.dialogParams?.categories || this.navCtrl.getParam('categories');
    this.categoryItems = categories.map((category) => ({
      value: category.id,
      viewValue: category.name,
      icon: category.default_icon_url,
      category
    }));
  }

  async createAddressForm() {
    try {
      const getAddressesFromBackend = false;
      const addresses = await this.client.getMoreDetailAddresses(getAddressesFromBackend);
      const selectedAddressId = await this.client.getSelectedAddressId(addresses)
      const includeAddAddress = false;
      this.addressItems = this.client.parseAddressList(addresses, includeAddAddress);
      this.form.patchValue({
        address: [selectedAddressId]
      });
      await this.addToDoListForm(selectedAddressId)
    } catch (err) {
      this.util.showError(this.getErrorMessage(err));
    }
  }

  updateInventoryType(type) {
    if (type === 'automatic') {
      this.form.controls.itemsPerReservation.setValidators([Validators.required]);
      this.form.controls.itemsPerReservation.updateValueAndValidity();
    } else {
      this.form.controls.itemsPerReservation.clearValidators();
      this.form.controls.itemsPerReservation.updateValueAndValidity();
    }
  }

  addRemoveAddress(addressIds) {
    addressIds.map((addressId) => {
      if (!this.toDoListsItems[addressId]) {
        this.addToDoListForm(addressId);
      }
    });
    this.selectedToDoLists.map((toDoList) => {
      if (!this.form.value.address.includes(toDoList.addressId)) {
        this.selectedToDoLists.splice(this.selectedToDoLists.indexOf(toDoList), 1);
      }
    });
  }

  async addToDoListForm(addressId) {
    this.isUpdatingToDoListsForms = true;
    this.toDoListsItems[addressId] = [];
    const toDoLists = await this.toDos.getAllToDoListsForAddress(addressId);
    await Promise.all(toDoLists.map(async (toDoList) => {
      this.toDoListsItems[addressId].push({
        value: toDoList.id,
        viewValue: toDoList.title
      });
      const rooms = await this.toDos.getListDetail(toDoList.id);
      this.toDoListsItems[toDoList.id] = [];
      rooms.map((room) => {
        this.toDoListsItems[toDoList.id].push({
          value: room.id,
          viewValue: room.name
        });
      });
    }));
    this.isUpdatingToDoListsForms = false;
  }

  async createToDoListForm() {
    this.toDoListsItems[this.item.address_id] = [];
    this.item.address_task_lists.map((list) => {
      this.toDoListsItems[this.item.address_id].push({
        value: list.id,
        viewValue: list.title
      });
      this.toDoListsItems[list.id] = [];
      list.address_task_groups.map((group) => {
        this.toDoListsItems[list.id].push({
          value: group.id,
          viewValue: group.name
        });
      });
    });
  }

  getAddressName(addressId) {
    const address = this.addressItems.find((addr) => addr.value === addressId);
    return address.viewValue;
  }

  listIsAdded(toDoListId) {
    return this.selectedToDoLists.find((list) => list.listId === toDoListId);
  }

  roomIsAdded(roomId, toDoListId) {
    return this.selectedToDoLists.find((list) => list.listId === toDoListId && list.rooms.includes(roomId));
  }

  updateSelectedToDoLists(toDoListId, addressId) {
    const toDoList = this.selectedToDoLists.find((list) => list.listId === toDoListId);
    if (!toDoList) {
      this.selectedToDoLists.push({addressId, listId: toDoListId, rooms: []});
    } else {
      const index = this.selectedToDoLists.findIndex(item => item.listId === toDoListId);
      if (index > -1) {
        this.selectedToDoLists.splice(index, 1);
      }
    }
  }

  updateSelectedRooms(roomId, toDoListId) {
    this.didNotSelectRoomError = false;
    this.refreshFormErrorState();
    this.selectedToDoLists.map((list) => {
      if (list.listId === toDoListId) {
        const room = list.rooms.find((id) => id === roomId);
        if (!room) {
          list.rooms.push(roomId);
        } else {
          const index = list.rooms.findIndex(item => item === roomId);
          if (index > -1) {
            list.rooms.splice(index, 1);
          }
        }
      }
    });
  }

  @Loading('', true)
  selectCharacteristic(characteristic, id) {
    try {
      this.characteristicsChosen[characteristic.name] = id;
      const query = this.buildQueryString(this.characteristicsChosen);
      this.getModels(query);
    } catch (err) {
      this.util.showError(this.getErrorMessage(err));
    }
  }

  @Loading('', true)
  async getModels(query) {
    const models = await this.genome.getObjectModel(this.category.id, query);
    this.models = models.map((model) => {
      const formData = {
        value: model.id,
        display: model.model
      };
      return formData;
    });
  }

  selectModel(model) {
    this.selectedModelID = model.value;
  }

  buildQueryString(obj) {
    let finalQuery = '';
    const finalArray = [];
    for (const key in obj) {
      if (obj[key] !== -1) {
        finalQuery += `&filters[characteristic_ids][]=${obj[key]}`;
        finalArray.push(obj[key]);
      }
    }
    this.characteristicsArray = finalArray;
    return finalQuery;
  }

  async addItem() {
    this.submitted = true;
    this.didNotSelectRoomError = false;
    this.errorMessage = null;
    if (this.form.value.inventoryUpdateType === 'task') {
      let addedRoom = false;
      this.selectedToDoLists.forEach((toDoList) => {
        if (toDoList.rooms.length > 0) {
          addedRoom = true;
        }
      });
      if (!addedRoom) {
        this.didNotSelectRoomError = true;
      }
    }
    if (this.hasIncompletePreferredBrand()) {
      this.refreshFormErrorState();
      this.util.showError('Please complete every preferred brand — brand, units per pack and a Walmart Business link are required.');
      return;
    }
    if (!this.form.valid || this.didNotSelectRoomError) {
      this.refreshFormErrorState();
      this.util.showError(this.getFirstFieldErrorMessage());
      return;
    }
    this.refreshFormErrorState();
    try {
      let restockingIntegrationId = null;
      if (this.form.value.whereToOrder && this.form.value.whereToOrder.startsWith('add:')) {
        const response = await this.addRestockingIntegration();
        restockingIntegrationId = response.id;
      }
      // Both save paths must be awaited: an un-awaited rejection escapes this
      // catch, which is how backend validation errors used to fail silently.
      if (this.item) {
        await this.updateItem(restockingIntegrationId);
      } else {
        await this.addNewItem(restockingIntegrationId);
      }
    } catch (err) {
      this.errorMessage = this.getErrorMessage(err);
      this.util.showError(this.errorMessage, 10000);
    }
  }

  // Backend rejections arrive as { code, message } from HttpClientCustom and as
  // { error: { message } } from Angular's HttpClient, so both shapes are read
  // before falling back — an "undefined" toast is no better than no toast. The
  // "Validation failed:" prefix is Rails' wording and means nothing to a customer.
  getErrorMessage(err): string {
    const message = err?.error?.message || err?.message;
    if (!message) {
      return 'Something went wrong saving this item. Please try again.';
    }
    return `${message}`.replace(/^Validation failed:\s*/i, '');
  }

  // The toast names the first thing that's wrong rather than saying "check the
  // form", since the offending field is often scrolled off screen. Each string
  // must match a translation key exactly (no added full stop), or the toast
  // shows in English.
  getFirstFieldErrorMessage(): string {
    if (this.form.controls.opportunisticOrderLevel.hasError('lessThanSibling')) {
      return 'The warning level must be greater than or equal to the min quantity';
    }
    if (this.form.controls.maxQuantity.hasError('lessThanSibling')) {
      return 'The target must be greater than or equal to the min quantity';
    }
    if (this.didNotSelectRoomError) {
      return 'Please select a checklist and card to add the item to';
    }
    return 'Please fill in all required fields';
  }

  async updateItem(restockingIntegrationId) {
    const data = {
      characteristic_ids: this.characteristicsArray,
      model_id: this.selectedModelID,
      photo_url: this.item.photo_url,
      inventory_settings: {
        strategies: []
      }
    }
    if (this.item?.consumable) {
      data['min_quantity'] = this.form.value.minQuantity;
      data['current_quantity'] = this.form.value.currentQuantity ? this.form.value.currentQuantity : null;
      data['max_quantity'] = this.form.value.maxQuantity ? this.form.value.maxQuantity : null;
      data['opportunistic_quantity'] = this.form.value.opportunisticOrderLevel ? this.form.value.opportunisticOrderLevel : null;
      data['delivery_timing_preference'] = this.form.value.deliveryTimingPreferences ? this.form.value.deliveryTimingPreferences : null;
      data['restocking_notes'] = this.form.value.restockingNotes ? this.form.value.restockingNotes : null;
      data['where_to_order'] = restockingIntegrationId || (this.form.value.whereToOrder ? this.form.value.whereToOrder : null);
      data['min_restocking_price'] = this.form.value.minRestockingPrice ? parseFloat(this.form.value.minRestockingPrice.replace('$', '')) : null;
      data['max_restocking_price'] = this.form.value.maxRestockingPrice ? parseFloat(this.form.value.maxRestockingPrice.replace('$', '')) : null;
      const selectedWarehouseProperty = this.warehousePropertyItems.find(
        (item) => item.value === this.form.value.warehouseProperty
      );
      data['warehouse_room_object_id'] = selectedWarehouseProperty?.roomObjectId || null;
      data['preferred_brands'] = this.normalizePreferredBrands();
      this.strategies.forEach((strategy) => {
        if (strategy === 'auto') {
          data['inventory_settings'].strategies.push({
            type: 'auto',
            amount_per_reservation: this.form.value.itemsPerReservation
          });
        }
        if (strategy === 'task') {
          data['inventory_settings'].strategies.push({
            type: 'task',
            inventory_type: 'count_remaining',
            address_task_lists: this.selectedToDoLists.map((toDoList) => ({
                address_task_list_id: toDoList.listId,
                address_task_group_ids: toDoList.rooms
              }))
          });
        }
      });
      if (data['inventory_settings'].strategies.length === 0) {
        delete data['inventory_settings'];
      }
    } else {
      data['min_quantity'] = null;
      data['current_quantity'] = null;
      data['max_quantity'] = null;
      data['delivery_timing_preference'] = null;
      data['restocking_notes'] = null;
      data['where_to_order'] = null;
      data['min_restocking_price'] = null;
      data['max_restocking_price'] = null;
      data['opportunistic_quantity'] = null;
      data['preferred_brands'] = [];
      delete data['inventory_settings'];
    }
    if (this.isAddingNewModel) {
      const modelName = this.form.value.model?.trim() || this.item.model.category.name;
      const modelData = {
        category_id: this.item.model.category.id,
        model: modelName,
        name: modelName,
        characteristic_ids: this.characteristicsArray
      }
      const newModel = await this.genome.addNewModel(modelData);
      data['model_id'] = newModel.id;
      data['name'] = modelName;
    }
    if (this.item.photo_url && !this.item.photo_url.includes('https://')) {
      data['photo_url'] = await this.uploadPhoto();
    }
    await this.genome.updateRoomObject(this.item.id, data);
    this.assetsQueries.invalidateAssets();
    const cameFromRoute = this.dialogParams?.cameFromRoute || this.navCtrl.getParam('cameFromRoute');
    if (this.isRightSideContent && cameFromRoute === 'order-restock') {
      // Launched from the Create Order cart in the right-side panel: goToItemSetup
      // pushed this edit form onto the panel stack, so return straight to the cart
      // (its draft is preserved) instead of pushing the read-only item page on top.
      // Otherwise the panel's back control unwinds through two item screens rather
      // than landing on the order the customer was building.
      await this.rightSidePanelService.goBack();
      return;
    }
    const params = {
      addressId: this.navCtrl.getParam('addressId') || this.dialogParams.addressId,
      itemId: this.item.id,
      cameFromEditItem: true
    };
    this.storage.save('shouldReloadPageAfterClosingSideRail', true);
    this.rightSidePanelService.navigateTo(`item-details/${this.item.id}`, params, ItemDetailsPage);
  }

  async addNewItem(restockingIntegrationId) {
    const data = {
      address_ids: this.form.value.address,
      room_objects: [{
        category_id: this.category.id,
        installation_date: `01/01/${this.form.value.installation_date}`,
        model_id: this.form.value.model,
        inventory_settings: {
          strategies: []
        }
      }]
    }
    if (this.photoURL) {
      data.room_objects[0]['photo_url'] = await this.uploadPhoto();
    }
    if (this.showConsumables) {
      data.room_objects[0]['min_quantity'] = this.form.value.minQuantity;
      data.room_objects[0]['current_quantity'] = this.form.value.currentQuantity ? this.form.value.currentQuantity : null;
      data.room_objects[0]['max_quantity'] = this.form.value.maxQuantity ? this.form.value.maxQuantity : null;
      data.room_objects[0]['opportunistic_quantity'] = this.form.value.opportunisticOrderLevel ? this.form.value.opportunisticOrderLevel : null;
      data.room_objects[0]['delivery_timing_preference'] = this.form.value.deliveryTimingPreferences ? this.form.value.deliveryTimingPreferences : null;
      data.room_objects[0]['restocking_notes'] = this.form.value.restockingNotes ? this.form.value.restockingNotes : null;
      data.room_objects[0]['where_to_order'] = restockingIntegrationId || (this.form.value.whereToOrder ? this.form.value.whereToOrder : null);
      data.room_objects[0]['min_restocking_price'] = this.form.value.minRestockingPrice ? parseFloat(this.form.value.minRestockingPrice.replace('$', '')) : null;
      data.room_objects[0]['max_restocking_price'] = this.form.value.maxRestockingPrice ? parseFloat(this.form.value.maxRestockingPrice.replace('$', '')) : null;
      data.room_objects[0]['preferred_brands'] = this.normalizePreferredBrands();
      this.strategies.forEach((strategy) => {
        if (strategy === 'auto') {
          data.room_objects[0]['inventory_settings'].strategies.push({
            type: 'auto',
            amount_per_reservation: this.form.value.itemsPerReservation
          });
        }
        if (strategy === 'task') {
          data.room_objects[0]['inventory_settings'].strategies.push({
            type: 'task',
            inventory_type: 'count_remaining',
            address_task_lists: this.selectedToDoLists.map((toDoList) => ({
                address_task_list_id: toDoList.listId,
                address_task_group_ids: toDoList.rooms
              }))
          });
        }
      });
      if (data.room_objects[0]['inventory_settings'].strategies.length === 0) {
        delete data.room_objects[0]['inventory_settings'];
      }
    } else {
      data.room_objects[0]['min_quantity'] = null;
      data.room_objects[0]['current_quantity'] = null;
      data.room_objects[0]['max_quantity'] = null;
      data.room_objects[0]['delivery_timing_preference'] = null;
      data.room_objects[0]['restocking_notes'] = null;
      data.room_objects[0]['where_to_order'] = null;
      data.room_objects[0]['min_restocking_price'] = null;
      data.room_objects[0]['max_restocking_price'] = null;
      data.room_objects[0]['opportunistic_quantity'] = null;
      data.room_objects[0]['preferred_brands'] = [];
      delete data.room_objects[0]['inventory_settings'];
    }
    if (!this.selectedModelID) {
      const modelName = this.form.value.model?.trim() || this.category.name;
      const modelData = {
        category_id: this.category.id,
        model: modelName,
        name: modelName,
        characteristic_ids: this.characteristicsArray
      }
      const newModel = await this.genome.addNewModel(modelData);
      data.room_objects[0].model_id = newModel.id;
    }
    await this.genome.addRoomObject(data);
    this.assetsQueries.invalidateAssets();
    if (Capacitor.isNativePlatform()) {
      this.navCtrl.back();
    } else {
      if (this.showConsumables) {
        this.navCtrl.navigateForward('restocking');
      } else {
        this.navCtrl.navigateForward('assets');
      }
    }
  }

  async uploadPhoto() {
    const uuid = uuidv4();
    const photo = await this.photoNotes.uploadPictureToS3(this.photoURL , `item/${uuid}`);
    return photo.Location;
  }

  buildInstallationDateItems() {
    const array: any = [];
    const year = LuxonDateTime.now().year;
    for (let i = year; i > (year - 232); i--) {
      array.push({
        value: i,
        viewValue: i
      });
    }
    return array;
  }

  getSalesCards() {
    return [
      {
        title: 'Get Maintenance Tips',
        text: 'When you add an item, we will tell you what the manufacturer recommends for that.',
        image: 'assets/img/tool-box.svg'
      },
      {
        title: 'Clean Smarter',
        text: 'Knowing what is in your home helps you and any pros you use follow best practices for that exact thing.',
        image: 'assets/img/light-bulb.svg'
      },
      {
        title: 'Track Maintenance',
        text: 'Tracking what was done makes it easier for future maintenance and repairs. It\'s like a medical record of your home!',
        image: 'assets/img/task-list.svg'
      }
    ];
  }

  // Thousands of items were saved with a target below their min (mostly the
  // placeholder 1 from the 08-11 backfill). Those keep saving while the person
  // leaves both numbers alone, so an unrelated edit is never blocked.
  validateTargetAtLeastMin = (control: AbstractControl): ValidationErrors | null => {
    const min = control.parent?.get('minQuantity')?.value;
    const unchanged = this.item &&
      `${control.value ?? ''}` === `${this.item.max_quantity ?? ''}` &&
      `${min ?? ''}` === `${this.item.min_quantity ?? ''}`;
    if (unchanged) {
      return null;
    }
    return validateGreaterThanOrEqual('minQuantity')(control);
  };

  setValidators() {
    if (this.showConsumables) {
      // A consumable can't be restocked without a reorder trigger (min), a target
      // level to top up to (max) and a price ceiling for the concierge (max price).
      this.form.controls.minQuantity.setValidators([Validators.required]);
      this.form.controls.maxQuantity.setValidators([Validators.required, this.validateTargetAtLeastMin]);
      this.form.controls.maxRestockingPrice.setValidators([Validators.required]);
      // RoomObject rejects an opportunistic quantity below the min quantity, so
      // enforce it here rather than letting the save round-trip to a 422.
      this.form.controls.opportunisticOrderLevel.setValidators([validateGreaterThanOrEqual('minQuantity')]);
      this.form.controls.minQuantity.updateValueAndValidity();
      this.form.controls.maxQuantity.updateValueAndValidity();
      this.form.controls.maxRestockingPrice.updateValueAndValidity();
      this.form.controls.opportunisticOrderLevel.updateValueAndValidity();
    } else {
      this.form.controls.minQuantity.clearValidators();
      this.form.controls.minQuantity.updateValueAndValidity();
      this.form.controls.maxQuantity.clearValidators();
      this.form.controls.maxQuantity.updateValueAndValidity();
      this.form.controls.maxRestockingPrice.clearValidators();
      this.form.controls.maxRestockingPrice.updateValueAndValidity();
      this.form.controls.opportunisticOrderLevel.clearValidators();
      this.form.controls.opportunisticOrderLevel.updateValueAndValidity();
      this.form.patchValue({
        deliveryTimingPreferences: null,
        opportunisticOrderLevel: null,
        whereToOrder: null,
        minRestockingPrice: null,
        maxRestockingPrice: null,
        restockingNotes: null,
        minQuantity: null,
        maxQuantity: null,
        currentQuantity: null
      });
    }
  }

  async addImage() {
    try {
      const newPhotoNote = await this.photoCaptionService.getImg('task');
      this.photoURL = newPhotoNote.url;
    } catch (_err) {
      // The second showError arg is the toast duration, not the error — passing
      // the error object there left the toast on screen forever.
      this.util.showError('Error uploading photo. Please try again.');
    }
  }

  removeImage(_event) {
    this.photoURL = null;
  }

  async selectCategory(categoryId) {
    this.category = this.categoryItems.find((item) => item.value === categoryId).category;
    if (this.showConsumables) {
      this.patchFormWithCategoryDefaults();
      await this.applyDefaultPreferredBrands(true);
    } else {
      const defaultYear = Genome.getDefaultInstallationYear(this.category.name);
      this.form.patchValue({
        installation_date: defaultYear
      });
    }
  }

  patchFormWithCategoryDefaults() {
    if (!this.category.defaults) {
      return;
    }
    Object.keys(this.category.defaults).forEach((key) => {
      if (this.category.defaults?.[key]) {
        this.form.patchValue({
          [key]: this.category.defaults[key]
        });
      }
    });
  }

  updateStrategies(strategy) {
    if (this.strategies.includes(strategy)) {
      this.strategies = this.strategies.filter((s) => s !== strategy);
    } else {
      this.strategies.push(strategy);
    }
  }

  // Errors bubble to addItem's catch on purpose: swallowing them here returned
  // undefined and the caller then failed on `response.id`, replacing the real
  // backend message with a TypeError.
  async addRestockingIntegration() {
    const payload = {
      guest_reservation_source_type_id: this.form.value.whereToOrder.split(':')[1],
      data: {
        username: this.form.value.conciergeLogin,
        password: this.form.value.conciergePassword,
        notes: this.form.value.conciergeNotes
      }
    }
    return await this.guestReservations.addConciergeReservationSource(payload);
  }
  
}